@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  /* ── Design tokens ──────────────────────────────────────────────────────
     Space-separated RGB triples so Tailwind can do `rgb(var(--x) / <alpha>)`.
     Light mockup palette on :root and .light; a complementary flat dark
     palette on .dark. A single class on <html> swaps the whole theme. */
  :root,
  .light {
    --bg: 245 245 242; /* #f5f5f2 editorial paper */
    --panel: 255 255 255; /* #ffffff */
    --rule: 216 216 207; /* #d8d8cf 1px hairline */
    --rule-soft: 230 230 222; /* inner dividers */
    --ink: 17 17 17; /* #111111 */
    --dim: 104 104 104; /* #686868 */
    --link: 58 58 58; /* #3a3a3a */
    --accent: 0 102 204; /* #0066cc evidence/action blue */
    --topbar: 245 245 242;
    --accent-soft: 237 237 234; /* light gray — hover/chip fill */
    --accent-ink: 0 82 164;
    --warn: 176 122 0;
    --good: 42 122 62;
    --del: 158 41 41;
    --add: 42 122 62;
    --code-bg: 246 248 250; /* #f6f8fa hljs light */
    --selection: 26 26 26;
  }

  .dark {
    --bg: 22 22 26; /* warm off-black newsprint */
    --panel: 29 29 34;
    --rule: 51 51 59;
    --rule-soft: 42 42 49;
    --ink: 237 237 237;
    --dim: 154 154 154;
    --link: 200 200 200;
    --accent: 90 167 255;
    --topbar: 24 24 24; /* keep header bar near-black in dark too, white text stays readable */
    --accent-soft: 42 42 49; /* dark gray — hover/chip fill, matches --rule-soft */
    --accent-ink: 123 186 255;
    --warn: 217 164 65;
    --good: 95 184 122;
    --del: 224 108 108;
    --add: 95 184 122;
    --code-bg: 13 17 23; /* github-dark-dimmed */
    --selection: 237 237 237;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    color: rgb(var(--ink));
    background: rgb(var(--bg));
    font-family:
      'Manrope',
      system-ui,
      -apple-system,
      sans-serif;
    font-size: 14px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
  }

  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  code,
  pre,
  kbd,
  samp {
    font-family: 'JetBrains Mono', 'Courier New', ui-monospace, monospace;
  }

  /* ── Heading scale (convention, not enforced) ────────────────────────────
     Use the named Tailwind sizes — never arbitrary `text-[Npx]`. Two tiers:

       App pages (default):
         page title    → text-lg  font-semibold
         section title → text-base font-semibold
         subsection    → text-sm  font-semibold

       Marketing pages (landing `/`, `/why`, SectionHeading):
         hero title    → text-3xl sm:text-4xl font-semibold  (or font-bold on
                         the dense landing lists)
         page hero     → text-xl / text-2xl

     Micro/label text uses text-2xs (11px) and text-3xs (10px). Body copy is
     text-sm; the base body size is 13px. */

  * {
    scrollbar-color: rgba(130, 130, 130, 0.35) transparent;
  }

  ::selection {
    background: rgb(var(--selection) / 0.22);
    color: inherit;
  }

  .dark ::selection {
    background: rgb(var(--selection) / 0.4);
    color: inherit;
  }
}

@layer components {
  .page-eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.14em] text-accent;
  }

  .page-title {
    @apply text-2xl font-semibold tracking-tight text-ink;
  }

  .page-lede {
    @apply mt-2 max-w-3xl text-sm leading-relaxed text-dim;
  }

  .lc-surface {
    @apply border border-rule bg-panel;
  }

  .lc-surface-muted {
    @apply border border-rule-soft bg-bg;
  }

  .lc-input {
    @apply w-full border border-rule bg-panel px-3 py-2 text-sm text-ink outline-none transition-colors;
    @apply placeholder:text-dim focus:border-accent focus:ring-1 focus:ring-accent;
  }

  .lc-chip {
    @apply border border-rule bg-bg px-2.5 py-1 text-xs font-medium text-dim transition-colors;
    @apply hover:bg-accent-soft hover:text-accent;
  }

  .lc-code-panel {
    @apply border border-rule bg-code-bg;
  }

  /* ── Mockup vocabulary ──────────────────────────────────────────────── */

  /* Uppercase letter-spaced section header bar. */
  .barhead {
    @apply flex items-center gap-2 border-b border-rule bg-accent-soft px-5 py-2 text-2xs font-semibold uppercase tracking-[0.12em] text-accent;
  }
  .barhead .sm {
    @apply font-normal normal-case tracking-normal text-dim;
  }

  /* Bordered, stacked-panel container — children are auto rule-separated. */
  .sheet {
    @apply border border-rule bg-panel;
  }
  .sheet-row {
    @apply px-4 py-3;
  }

  /* Square flat chips. */
  .chip {
    @apply inline-flex items-center border border-rule bg-bg px-2 py-0.5 text-xs text-dim;
  }
  .chip-accent {
    @apply inline-flex items-center border border-accent/40 bg-accent-soft px-2 py-0.5 text-xs text-accent;
  }

  /* Buttons are the <Button> component (components/ui/Button.tsx) — the single
     source of truth for primary/secondary/ghost CTAs. The former .btn-flat /
     .btn-flat-secondary utility classes were removed in favour of it. */

  /* Two-column demo grid with a center divider rule. */
  .demo-grid {
    @apply grid grid-cols-1 md:grid-cols-2;
  }
  .demo-grid > * + * {
    @apply border-t border-rule md:border-l md:border-t-0;
  }

  .topbar {
    @apply border-b border-rule bg-topbar/90 text-ink backdrop-blur-xl;
  }

  .eyebrow {
    @apply text-3xs font-semibold uppercase tracking-[0.18em] text-dim;
  }

  /* Markdown preview inside the chunk panel */
  /* ── Query answer prose ───────────────────────────────────────────────── */
  .lc-answer {
    @apply text-sm text-link;
  }
  .lc-answer p {
    @apply mb-4 leading-7;
  }
  .lc-answer p:last-child {
    @apply mb-0;
  }
  .lc-answer h1,
  .lc-answer h2,
  .lc-answer h3,
  .lc-answer h4,
  .lc-answer h5,
  .lc-answer h6 {
    @apply font-semibold text-ink mt-6 mb-2 leading-snug;
  }
  .lc-answer h1 {
    @apply text-base;
  }
  .lc-answer h2 {
    @apply text-sm;
  }
  .lc-answer h3,
  .lc-answer h4 {
    @apply text-sm text-link;
  }
  .lc-answer ul,
  .lc-answer ol {
    @apply my-4 pl-5 space-y-1.5;
  }
  .lc-answer ul {
    @apply list-disc;
  }
  .lc-answer ol {
    @apply list-decimal;
  }
  .lc-answer li {
    @apply leading-7;
  }
  .lc-answer a {
    @apply text-accent underline underline-offset-2 decoration-accent/40;
  }
  .lc-answer a:hover {
    @apply text-accent decoration-accent;
  }
  .lc-answer strong {
    @apply font-semibold text-ink;
  }
  .lc-answer em {
    @apply italic;
  }
  .lc-answer blockquote {
    @apply border-l-2 border-accent/40 pl-4 my-4 text-dim;
  }
  .lc-answer hr {
    @apply border-rule my-5;
  }
  .lc-answer table {
    @apply w-full text-xs border-collapse my-4;
  }
  .lc-answer th,
  .lc-answer td {
    @apply border border-rule px-3 py-1.5 text-left;
  }
  .lc-answer th {
    @apply bg-bg font-semibold;
  }
  /* Inline code */
  .lc-answer :not(pre) > code {
    @apply bg-accent-soft px-1.5 py-0.5 font-mono text-xs text-accent;
  }
  /* Code blocks */
  .lc-answer pre {
    @apply my-4 overflow-x-auto p-0;
  }
  .lc-answer pre .hljs {
    @apply p-4 text-xs leading-[1.65];
  }
  /* GFM footnote citations */
  .lc-answer sup a {
    @apply rounded-full bg-accent-soft px-1.5 py-0.5 font-mono text-3xs font-semibold text-accent no-underline decoration-transparent;
  }
  .lc-answer sup a:hover {
    @apply bg-accent/20;
  }
  .lc-answer .footnotes {
    @apply mt-6 border-t border-rule pt-4 text-xs;
  }
  .lc-answer .footnotes h2 {
    @apply mb-2 text-2xs font-semibold uppercase tracking-wider text-dim;
  }
  .lc-answer .footnotes ol {
    @apply my-0 space-y-1 pl-5;
  }
  .lc-answer .footnotes li {
    @apply leading-relaxed text-dim;
  }
  .lc-answer .footnotes li p {
    @apply m-0 inline;
  }

  /* Larger-scale variant for the landing demo answer panel. */
  .lc-answer-lg {
    @apply text-sm leading-[1.75];
  }
  .lc-answer-lg p {
    @apply leading-[1.75];
  }
  .lc-answer-lg li {
    @apply leading-[1.75];
  }

  /* Light-mode override for highlight.js (which ships with github-dark-dimmed). */
  /* Uses :where() so specificity stays low and dark-mode defaults win when .dark is set. */
  :where(html:not(.dark)) .hljs {
    background: #f6f8fa;
    color: #1f2328;
  }
  :where(html:not(.dark)) .hljs-comment,
  :where(html:not(.dark)) .hljs-quote {
    color: #6e7781;
    font-style: italic;
  }
  :where(html:not(.dark)) .hljs-keyword,
  :where(html:not(.dark)) .hljs-selector-tag,
  :where(html:not(.dark)) .hljs-subst,
  :where(html:not(.dark)) .hljs-operator {
    color: #cf222e;
  }
  :where(html:not(.dark)) .hljs-string,
  :where(html:not(.dark)) .hljs-regexp,
  :where(html:not(.dark)) .hljs-meta-string {
    color: #0a3069;
  }
  :where(html:not(.dark)) .hljs-number,
  :where(html:not(.dark)) .hljs-literal,
  :where(html:not(.dark)) .hljs-variable.constant_,
  :where(html:not(.dark)) .hljs-template-variable,
  :where(html:not(.dark)) .hljs-attr,
  :where(html:not(.dark)) .hljs-attribute {
    color: #0550ae;
  }
  :where(html:not(.dark)) .hljs-title,
  :where(html:not(.dark)) .hljs-title.function_,
  :where(html:not(.dark)) .hljs-section,
  :where(html:not(.dark)) .hljs-name,
  :where(html:not(.dark)) .hljs-selector-id,
  :where(html:not(.dark)) .hljs-selector-class {
    color: #8250df;
  }
  :where(html:not(.dark)) .hljs-built_in,
  :where(html:not(.dark)) .hljs-type,
  :where(html:not(.dark)) .hljs-symbol,
  :where(html:not(.dark)) .hljs-bullet {
    color: #953800;
  }
  :where(html:not(.dark)) .hljs-tag {
    color: #116329;
  }
  :where(html:not(.dark)) .hljs-params,
  :where(html:not(.dark)) .hljs-punctuation {
    color: #1f2328;
  }
  :where(html:not(.dark)) .hljs-deletion {
    color: #82071e;
    background: #ffebe9;
  }
  :where(html:not(.dark)) .hljs-addition {
    color: #116329;
    background: #dafbe1;
  }
  :where(html:not(.dark)) .hljs-emphasis {
    font-style: italic;
  }
  :where(html:not(.dark)) .hljs-strong {
    font-weight: 600;
  }

  .lc-md-preview {
    @apply text-sm text-link leading-relaxed;
  }
  .lc-md-preview h1,
  .lc-md-preview h2,
  .lc-md-preview h3,
  .lc-md-preview h4,
  .lc-md-preview h5,
  .lc-md-preview h6 {
    @apply font-semibold text-ink mt-4 mb-1;
  }
  .lc-md-preview h1 {
    @apply text-lg;
  }
  .lc-md-preview h2 {
    @apply text-base;
  }
  .lc-md-preview h3 {
    @apply text-sm;
  }
  .lc-md-preview p {
    @apply mb-2;
  }
  .lc-md-preview ul,
  .lc-md-preview ol {
    @apply mb-2 pl-5;
  }
  .lc-md-preview ul {
    @apply list-disc;
  }
  .lc-md-preview ol {
    @apply list-decimal;
  }
  .lc-md-preview li {
    @apply mb-0.5;
  }
  .lc-md-preview code {
    @apply rounded bg-accent-soft px-1 py-0.5 font-mono text-xs text-accent;
  }
  .lc-md-preview pre {
    @apply mb-3 overflow-auto rounded border border-rule bg-code-bg p-3 font-mono text-xs text-link;
  }
  .lc-md-preview pre code {
    @apply bg-transparent p-0 text-link;
  }
  .lc-md-preview blockquote {
    @apply border-l-2 border-rule pl-3 italic text-dim;
  }
  .lc-md-preview a {
    @apply text-accent underline underline-offset-2;
  }
  .lc-md-preview table {
    @apply w-full border-collapse text-xs;
  }
  .lc-md-preview th,
  .lc-md-preview td {
    @apply border border-rule px-2 py-1;
  }
  .lc-md-preview th {
    @apply bg-bg font-semibold;
  }
  .lc-md-preview hr {
    @apply border-rule my-3;
  }
}

@layer utilities {
  .text-balance {
    text-wrap: balance;
  }
}
